import { Padding } from "@mui/icons-material";
import { createTheme } from "@mui/material";

const colorTheme = createTheme({
    typography: {
        fontFamily: [
            '"Segoe UI"',
            'Roboto',
            '-apple-system',
            'BlinkMacSystemFont',
            'Arial',
            'sans-serif'
        ].join(','),
        h1: {
            fontWeight: 300,
            fontSize: '3rem',        // 48px
            lineHeight: 1.15,
            letterSpacing: '-0.015em',
        },
        h2: {
            fontWeight: 300,
            fontSize: '2.25rem',     // 36px
            lineHeight: 1.2,
            letterSpacing: '-0.008em',
        },
        h3: {
            fontWeight: 400,
            fontSize: '1.875rem',    // 30px
            lineHeight: 1.18,
            letterSpacing: '0em',
        },
        h4: {
            fontWeight: 400,
            fontSize: '1.5rem',      // 24px
            lineHeight: 1.22,
            letterSpacing: '0.007em',
        },
        h5: {
            fontWeight: 500,
            fontSize: '1.20rem',     // 20px
            lineHeight: 1.25,
            letterSpacing: '0em',
        },
        h6: {
            fontWeight: 600,
            fontSize: '1.0625rem',        // 16px
            lineHeight: 1.35,
            letterSpacing: '0.007em',
        },
        body1: {
            fontWeight: 400,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.009em',
        },
        body2: {
            fontWeight: 400,
            fontSize: '0.875rem',
            lineHeight: 1.43,
            letterSpacing: '0.01em',
        },
        button: {
            fontWeight: 500,
            fontSize: '0.95rem',
            letterSpacing: '0.02em',
            textTransform: 'none' as const,
            lineHeight: 1.75,
        }
    },
    palette: {
        primary: {
            main: '#001B2D',       // Midnight
            light: '#01385D',      // Prussian
            dark: '#045D8B',       // Venice
            contrastText: '#FFFFFF'
        },

        secondary: {
            main: '#045D8B',       // Venice
            light: '#008BBA',      // Bondi
            dark: '#01385D',       // Prussian
            contrastText: '#FFFFFF'
        },

        error: {
            main: '#E12619',       // Brand error red
            light: '#F5B4B0',
            dark: '#B31E14',
            contrastText: '#FFFFFF'
        },

        warning: {
            main: '#FFC107',       // Brand yellow
            light: '#FFDE6B',
            dark: '#E0A800',
            contrastText: '#001B2D'
        },

        success: {
            main: '#126B40',       // Brand green
            light: '#6FC693',
            dark: '#0A4A2D',
            contrastText: '#FFFFFF'
        },

        info: {
            main: '#00BFE8',       // Brand cyan
            light: '#00BFE8',
            dark: '#008BA8',
            contrastText: '#FFFFFF'
        },

        background: {
            default: '#FAFAFA',
            paper: '#FFFFFF'
        },

        text: {
            primary: '#001B2D',
            secondary: 'rgba(0,0,0,0.64)',
            disabled: 'rgba(0,0,0,0.38)'
        },

        divider: 'rgba(0, 0, 0, 0.12)'
    },
    shape: {
        borderRadius: 8
    },
    shadows: [
        'none',
        '0px 2px 4px rgba(0, 0, 0, 0.05)',
        '0px 4px 8px rgba(0, 0, 0, 0.1)',
        '0px 8px 16px rgba(0, 0, 0, 0.1)',
        '0px 12px 24px rgba(0, 0, 0, 0.15)',
        '0px 16px 32px rgba(0, 0, 0, 0.15)',
        '0px 20px 40px rgba(0, 0, 0, 0.2)',
        '0px 24px 48px rgba(0, 0, 0, 0.2)',
        '0px 28px 56px rgba(0, 0, 0, 0.25)',
        '0px 32px 64px rgba(0, 0, 0, 0.25)',
        '0px 36px 72px rgba(0, 0, 0, 0.3)',
        '0px 40px 80px rgba(0, 0, 0, 0.3)',
        '0px 44px 88px rgba(0, 0, 0, 0.35)',
        '0px 48px 96px rgba(0, 0, 0, 0.35)',
        '0px 52px 104px rgba(0, 0, 0, 0.4)',
        '0px 56px 112px rgba(0, 0, 0, 0.4)',
        '0px 60px 120px rgba(0, 0, 0, 0.45)',
        '0px 64px 128px rgba(0, 0, 0, 0.45)',
        '0px 68px 136px rgba(0, 0, 0, 0.5)',
        '0px 72px 144px rgba(0, 0, 0, 0.5)',
        '0px 76px 152px rgba(0, 0, 0, 0.55)',
        '0px 80px 160px rgba(0, 0, 0, 0.55)',
        '0px 84px 168px rgba(0, 0, 0, 0.6)',
        '0px 88px 176px rgba(0, 0, 0, 0.6)',
        '0px 92px 184px rgba(0, 0, 0, 0.65)'
    ],
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#143A6B',
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 24,
                    textTransform: 'none',
                    fontWeight: 500,

                    '&:hover': {
                        boxShadow: 'none',// primary.dark
                    },
                },
                contained: {
                    background: '#001B2D', // Midnight - primary button color
                    color: '#FFFFFF',      // White text on dark background
                    borderRadius: 24,      // rounded-full
                    fontWeight: 500,

                    '&:hover': {
                        background: '#00BFE8', // Cyan - hover state for buttons
                    },
                    '&:active': {
                        background: '#045D8B', // Venice - pressed state
                    },
                    '&:disabled': {
                        background: '#E0E0E0', // Neutral disabled bg (avoid brand colors)
                        color: '#BDBDBD',
                        boxShadow: 'none',
                        cursor: 'not-allowed'
                    },
                },
                containedSuccess: {
                    background: '#4AB15F', // success.main
                    color: '#fff',
                    '&:hover': {
                        background: '#357A45', // success.dark
                    },
                    '&:active': {
                        background: '#357A45', // success.dark
                    },
                    '&:disabled': {
                        background: '#d1f8d9ff', // success.light
                        color: '#bdbdbd',
                    },
                },
                containedError: {
                    background: '#C33E38', // error.main
                    color: '#fff',
                    '&:hover': {
                        background: '#A12D27', // error.dark
                    },
                    '&:active': {
                        background: '#A12D27', // error.dark
                    },
                    '&:disabled': {
                        background: '#e0e0e0',
                        color: '#bdbdbd',
                    },
                },
                containedWarning: {
                    background: '#E68231', // warning.main
                    color: '#fff',
                    '&:hover': {
                        background: '#C2661A', // warning.dark
                    },
                    '&:active': {
                        background: '#C2661A', // warning.dark
                    },
                    '&:disabled': {
                        background: '#e0e0e0',
                        color: '#bdbdbd',
                    },
                },
                outlined: {
                    background: '#FFFFFF',
                    color: '#001B2D',                 // Midnight text
                    border: '1px solid #001B2D',      // Midnight stroke
                    boxSizing: 'border-box',          // keep border inside padding

                    '&:hover': {
                        background: '#FFFFFF',
                        border: '1px solid #00BFE8',    // Cyan accent on hover
                        color: '#00BFE8',               // Cyan text to match hover accent
                    },

                    '&:active': {
                        background: '#E5F8FD',          // Cyan 5% tint (subtle pressed bg)
                        border: '1px solid #045D8B',    // Venice stroke for pressed
                        color: '#045D8B',               // Venice text for pressed
                    },

                    '&:disabled': {
                        background: '#FAFAFA',
                        color: '#BDBDBD',
                        border: '1px solid #E0E0E0',
                        boxShadow: 'none',
                        cursor: 'not-allowed',
                    },
                },
                outlinedError: {
                    color: '#C33E38', // error.main
                    border: '1px solid #C33E38',
                    '&:hover': {
                        background: '#fff',
                        border: '1px solid #A12D27', // error.dark
                        color: '#A12D27',
                    },
                    '&:active': {
                        background: '#fff0f0',
                        border: '1px solid #A12D27',
                        color: '#A12D27',
                    },
                },
                outlinedWarning: {
                    color: '#E68231', // warning.main
                    border: '1px solid #E68231',
                    '&:hover': {
                        background: '#fff',
                        border: '1px solid #C2661A', // warning.dark
                        color: '#C2661A',
                    },
                    '&:active': {
                        background: '#fff8f0',
                        border: '1px solid #C2661A',
                        color: '#C2661A',
                    },
                },
                text: {
                    color: '#001B2D', // Midnight - primary text button color

                    '&:hover': {
                        background: 'rgba(0, 191, 232, 0.08)', // Cyan tint (brand hover state)
                        color: '#001B2D',                       // Keep Midnight text for contrast
                    },

                    '&:active': {
                        background: 'rgba(4, 93, 139, 0.12)', // Venice tint for active state
                        color: '#045D8B',                      // Venice text for pressed state
                    },

                    '&:disabled': {
                        color: '#BDBDBD', // Neutral disabled text
                        background: 'transparent',
                    },
                },
                sizeLarge: {
                    padding: '10px 28px',
                    fontSize: '1rem', // 18px
                },
                sizeMedium: {
                    padding: '8px 20px',
                    fontSize: '0.9375rem', // 15px
                },
                sizeSmall: {
                    padding: '6px 16px',
                    fontSize: '0.875rem', // 14px,
                },
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    margin: '2px 8px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'rgba(20, 59, 107, 0.08)', // primary.main with opacity
                        transform: 'translateX(4px)'
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(86, 147, 247, 0.2)',
                        borderLeft: '3px solid #5693F7',
                        '&:hover': {
                            backgroundColor: 'rgba(86, 147, 247, 0.3)'
                        }
                    }
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontWeight: 500,       // Make NavLink text bold
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                },
                elevation1: {
                    boxShadow: '0px 2px 4px rgba(20, 59, 107, 0.05)' // primary.main shadow
                },
                elevation2: {
                    boxShadow: '0px 4px 8px rgba(20, 59, 107, 0.08)'
                },
                elevation3: {
                    boxShadow: '0px 8px 16px rgba(20, 59, 107, 0.1)'
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    padding: 8,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        transform: 'scale(1.05)',
                        '& .MuiSvgIcon-root': {
                            filter: 'drop-shadow(0 2px 4px rgba(20, 59, 107, 0.15))' // primary.main shadow
                        }
                    },
                    '&.Mui-checked': {
                        color: '#001B2D', // primary.main
                        '&:hover': {
                            backgroundColor: 'transparent',
                            '& .MuiSvgIcon-root': {
                                filter: 'drop-shadow(0 2px 6px rgba(20, 59, 107, 0.2))'
                            }
                        }
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.3rem',
                        transition: 'all 0.2s ease-in-out'
                    }
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    borderRadius: '50%',
                    padding: 8,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        transform: 'scale(1.05)',
                        '& .MuiSvgIcon-root': {
                            filter: 'drop-shadow(0 2px 4px rgba(20, 59, 107, 0.15))' // primary.main shadow
                        }
                    },
                    '&.Mui-checked': {
                        color: '#001B2D', // primary.main
                        '&:hover': {
                            backgroundColor: 'transparent',
                            '& .MuiSvgIcon-root': {
                                filter: 'drop-shadow(0 2px 6px rgba(20, 59, 107, 0.2))'
                            }
                        }
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.3rem',
                        transition: 'all 0.2s ease-in-out'
                    }
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    margin: '2px 0',
                    borderRadius: 12,
                    padding: '4px 8px',
                },
                label: {
                    fontSize: '0.95rem',
                    fontWeight: 400,
                    userSelect: 'none',
                    transition: 'all 0.2s ease-in-out',
                    opacity: 0.8
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    '@media (max-width: 768px)': {
                        margin: '8px',
                        borderRadius: 8
                    },
                    '@media (max-width: 480px)': {
                        margin: '4px',
                        borderRadius: 6
                    }
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '10px 20px 8px 20px'
                },
                title: {
                    fontSize: '1rem', // 17px, matches h6
                    fontWeight: 600,
                    lineHeight: 1.35,
                    letterSpacing: '0.007em',
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '0 20px 16px 20px',
                    '&:last-child': {
                        paddingBottom: 16
                    }
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '8px 20px 16px 20px',
                    gap: 8
                }
            }
        },
        MuiAccordionSummary: {
        styleOverrides: {
            root: {
                minHeight: 36,
                paddingTop: 4,
                paddingBottom: 4,
                paddingLeft: 12,
                paddingRight: 12,
                '&.Mui-expanded': {
                    minHeight: 36,
                }
            },
            content: {
                margin: 0,
                '&.Mui-expanded': {
                    margin: 0,
                }
            }
        }
    },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: 48,
                },
                indicator: {
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "#001B2D", // Show active border (primary.main) under selected tab
                    height: 2.5, // Thicker for visibility
                    borderRadius: 2,
                },
                scrollButtons: {
                    color: "#001B2D",
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    fontSize: "16.3px",
                    letterSpacing: 0.8,
                    minHeight: 48,
                    textTransform: "none",
                    "&.Mui-selected": {
                        color: "#001B2D",
                    },
                    "&.MuiButtonBase-root": {
                        WebkitTapHighlightColor: "transparent",
                        padding: "0px 0px 0px 0px",
                    },
                    marginRight: "18px",
                    ":last-of-type": {
                        marginRight: 0, 
                    }
                }
            },
            defaultProps: {
                disableRipple: true,
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    "&::placeholder": {
                        fontSize: "1rem", // Reduce placeholder font size
                        opacity: 0.6, // Ensure placeholder is visible
                    }
                }
            }
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    display: 'flex',         
                    alignItems: 'center',   
                    paddingLeft: '16px !important',
                    paddingRight: '16px !important',
                    paddingTop: '0 !important',
                    paddingBottom: '0 !important',
                    fontSize: '0.92rem',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: `${theme.palette.info.main}15`, // Cyan hover background from theme
                    }
                })
            }
        },
        MuiTextField: {
            defaultProps: {
                onKeyDown: (e: React.KeyboardEvent) => {
                    e.stopPropagation(); // Allow typing without parent interference globally
                }
            }
        }
    }
})

export default colorTheme;